import React, { useState, useMemo } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { Student } from '../../types';
import { StudentTable, StudentDetails } from './components';
import { SearchBar } from './components/SearchBar';
import { mockStudentData, mockStudents } from '@/data/mockStudents';
import { storageService } from '@/services/storageService';

export const StudentManagement: React.FC = () => {
  const [students, setStudents] = useState<Student[]>(mockStudents);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAddingStudent, setIsAddingStudent] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredStudents = useMemo(() => {
    if (!searchQuery.trim()) return students;

    const query = searchQuery.toLowerCase().trim();
    return students.filter(student => {
      const fullName = `${student.firstName} ${student.lastName}`.toLowerCase();
      const id = student.id.toLowerCase();
      const grade = `grade ${student.grade}`.toLowerCase();
      
      return (
        fullName.includes(query) ||
        id.includes(query) ||
        grade.includes(query) ||
        student.firstName.toLowerCase().includes(query) ||
        student.lastName.toLowerCase().includes(query)
      );
    });
  }, [students, searchQuery]);

  const loadStudentWithPhoto = (student: Student): Student => {
    const storedPhoto = storageService.getPhoto(student.id);
    if (storedPhoto) {
      return { ...student, photo: storedPhoto };
    }
    return student;
  };

  const handleEditStudent = (id: string) => {
    const student = students.find(s => s.id === id);
    if (student) {
      setSelectedStudent(loadStudentWithPhoto(student));
      setIsAddingStudent(false);
      setIsDialogOpen(true);
    }
  };

  const handleDeleteStudent = (id: string) => {
    if (window.confirm('Are you sure you want to delete this student?')) {
      storageService.removePhoto(id);
      setStudents(prev => prev.filter(student => student.id !== id));
      if (selectedStudent?.id === id) {
        setSelectedStudent(null);
        setIsDialogOpen(false);
      }
    }
  };

  const handleAddStudent = () => {
    const newId = `ST${new Date().getFullYear()}${String(students.length + 1).padStart(3, '0')}`;
    const emptyStudent: Student = {
      ...mockStudentData,
      id: newId,
      firstName: '',
      lastName: '',
      grade: 9,
      attendanceRate: 100,
      guardians: [],
      attendanceRecords: [],
      interventions: [],
      riskFactors: [],
      academicPerformance: {
        gpa: 0,
        credits: 0,
        coursesFailing: 0
      }
    };
    setSelectedStudent(emptyStudent);
    setIsAddingStudent(true);
    setIsDialogOpen(true);
  };

  const handleSaveStudent = async (formData: Partial<Student>) => {
    try {
      if (isAddingStudent) {
        const newStudent = {
          ...formData,
          id: formData.id || `ST${new Date().getFullYear()}${String(students.length + 1).padStart(3, '0')}`
        } as Student;

        // TODO: Check how to save photos
        // if (formData.photo instanceof File) {
        //   try {
        //     const photoUrl = await storageService.savePhoto(newStudent.id, formData.photo);
        //     newStudent.photo = photoUrl;
        //   } catch (error) {
        //     console.error('Failed to save photo:', error);
        //   }
        // }

        setStudents(prev => [...prev, newStudent]);
      } else {
        // if (formData.photo instanceof File && formData.id) {
        //   try {
        //     const photoUrl = await storageService.replacePhoto(formData.id, formData.photo);
        //     formData.photo = photoUrl;
        //   } catch (error) {
        //     console.error('Failed to replace photo:', error);
        //   }
        // }
        
        setStudents(prev => prev.map(s => s.id === formData.id ? { ...s, ...formData } : s));
        setSelectedStudent(prev => prev ? { ...prev, ...formData } : null);
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving student:', error);
    }
  };

  const handleCloseDialog = () => {
    setSelectedStudent(null);
    setIsDialogOpen(false);
    setIsAddingStudent(false);
  };

  const handleNavigate = (studentId: string) => {
    const student = students.find(s => s.id === studentId);
    if (student) {
      setSelectedStudent(loadStudentWithPhoto(student));
    }
  };

  return (
    <Stack spacing={3} sx={{ p: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box component="div" sx={{ typography: 'h4', fontWeight: 500 }}>
          Student Management
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddStudent}
        >
          Add Student
        </Button>
      </Box>

      <SearchBar 
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search by name, ID, or grade..."
      />

      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <StudentTable 
          students={filteredStudents}
          onEdit={handleEditStudent}
          onDelete={handleDeleteStudent}
        />
      </Box>

      {isDialogOpen && selectedStudent && (
        <StudentDetails
          student={selectedStudent}
          onSave={handleSaveStudent}
          onCancel={handleCloseDialog}
          isNew={isAddingStudent}
          allStudents={students}
          filteredStudents={searchQuery ? filteredStudents : undefined}
          onNavigate={handleNavigate}
        />
      )}
    </Stack>
  );
};